<template>
  <Row :inPackage="inPackage">
    <template #service>
      <BaseText
        :color="
            !selectedService
              ? isFormSubmitted
                ? 'error'
                : 'secondary'
              : inPackage
                ? 'secondary'
                : 'default'
          "
        :bold="isSmallScreen && !inPackage"
      >
        {{ selectedService
              ? selectedService.name
              : $t('appointment.create.select_service') }}
      </BaseText>
      <BaseIcon
        v-if="unavailableService"
        name="calendar"
        color="warning"
        size="s"
        v-test="'icon-warning-service'"
      />
    </template>

    <template #duration>
      <BaseText :color="inPackage ? 'secondary' : 'default'">
        {{ `${totalDuration} ${$t('global.minute_short')}` }}
      </BaseText>
    </template>

    <template
      #resources
      v-if="!isSmallScreen || !inPackage || (isFormSubmitted && missingResourceAmount)"
    >
      <div v-if="missingResourceAmount">
        <BaseText :color="isFormSubmitted ? 'error' : 'secondary'">
          {{ missingResourceAmount === 1
              ? $t('appointment.create.select_resource')
              : $t('appointment.create.select_resources', {
                  amount: missingResourceAmount
                }) }}
        </BaseText>
      </div>
      <SelectedResource
        v-else
        :selectedResourceIds="selectedResourceIds"
        :color="inPackage ? 'secondary' : 'default'"
      />
      <Flex v-if="showRequirementWarning || showAvailabilityWarning">
        <BaseIcon
          v-if="showRequirementWarning"
          name="alert"
          color="error"
          size="s"
          v-test="'icon-warning-requirement'"
        />
        <BaseIcon
          v-if="showAvailabilityWarning"
          name="calendar"
          color="warning"
          size="s"
          v-test="'icon-warning-availability'"
        />
      </Flex>
    </template>
  </Row>
</template>

<script
  lang="ts"
  setup
>
import { storeToRefs } from 'pinia';
import { computed } from 'vue';
import {
  canAllocationBeAny,
  getPartDuration,
  getPartResources,
  getValidAllocationResourceIds,
} from '@/modules/calendar/appointment/helpers';
import { isSmallScreen } from '@/modules/calendar/appointment/helpers.ts';
import { useCreateAppointmentStore } from '@/stores/calendar-create-appointment';
import type { AppointmentPartAttributes, Resource } from '@/types';
import Row from './Row.vue';
import SelectedResource from './SelectedResource.vue';

const props = defineProps<{
  part: AppointmentPartAttributes;
  partIndex: number;
}>();

const { services } = useCreateAppointmentStore();
const { isFormSubmitted, unavailableServices } = storeToRefs(
  useCreateAppointmentStore(),
);

const selectedService = computed(() =>
  services.find((service) => service.id === props.part.serviceId),
);
const unavailableService = computed(() =>
  unavailableServices.value.find((s) => s.id === props.part.serviceId),
);
const totalDuration = computed(() => getPartDuration(props.part));
const selectedResourceIds = computed<Resource['id'][]>(() =>
  getPartResources(props.part),
);
// Empty allocations that the resource dropdown requires. One that can be left on "Any"
// doesn't count, using the same rule as content/ResourceSelect.vue.
const missingResourceAmount = computed(() => {
  const { requirementById, resources, partAvailabilities } =
    useCreateAppointmentStore();
  const allAllocations = props.part.allocationsAttributes;

  return allAllocations.filter((allocation, index) => {
    if (allocation.resourceId) {
      return false;
    }

    const validResourceIds = getValidAllocationResourceIds({
      allocation,
      allAllocations,
      requirement: allocation.requirementId
        ? requirementById(allocation.requirementId)
        : null,
      resources,
      availableResourceIds:
        partAvailabilities[props.partIndex]?.availableResourceIds,
    });

    return !canAllocationBeAny(index, validResourceIds);
  }).length;
});
const showRequirementWarning = computed(() => {
  if (!props.part.serviceId || missingResourceAmount.value) {
    return false;
  }

  const { allRequirements } = useCreateAppointmentStore();

  const serviceRequirements = allRequirements.filter(
    (req) => req.serviceId === props.part.serviceId,
  );

  const requirementResources = serviceRequirements
    .map((p) => p.resourceIds)
    .reduce((acc, val) => acc.concat(val), []);

  return !!selectedResourceIds.value.find(
    (id) => !requirementResources.includes(id),
  );
});

const showAvailabilityWarning = computed(() => {
  if (!props.part.serviceId || missingResourceAmount.value) {
    return false;
  }

  const { partAvailabilities } = useCreateAppointmentStore();
  const availabileResourceIds =
    partAvailabilities[props.partIndex]?.availableResourceIds;

  if (!availabileResourceIds) {
    return false;
  }

  return !!selectedResourceIds.value.find(
    (id) => !availabileResourceIds.includes(id),
  );
});

const inPackage = computed(() => !!props.part.servicePackageServiceId);
</script>
